export const recorderPreferencesVersion = 1 as const;

export const recorderPreferenceKeys = {
  isLive: '__useRecorderSettings.isLiveAtom',
  isCursorEnabled: '__useRecorderSettings.isCursorEnabledAtom',
  resolution: '__useRecorderSettings.resolutionAtom',
  bitrate: '__useRecorderSettings.bitrateAtom',
  liveBitrate: '__useRecorderSettings.liveBitrateAtom',
  camera: '__useRecorderSettings.cameraAtom',
  cameraPosition: '__useRecorderSettings.cameraPositionAtom',
  audioInput: '__useRecorderSettings.audioInputAtom',
  audioInputPreference: '__useRecorderSettings.audioInputPreferenceAtom',
  microphoneVolume: 'persistent__useRecorderSettings.audioInputVolumeAtom',
  audioInputEchoSuppression:
    '__useRecorderSettings.audioInputEchoSuppressionAtom',
  audioInputAutoGainControl:
    '__useRecorderSettings.audioInputAutoGainControlAtom',
  audioInputNoiseSuppression:
    '__useRecorderSettings.audioInputNoiseSuppressionAtom',
  audioInputVoiceMasking: '__useRecorderSettings.audioInputVoiceMaskingAtom',
  audioOutput: '__useRecorderSettings.audioOutputAtom',
  audioOutputVolume: '__useRecorderSettings.audioOutputVolumeAtom',
  autoSelectVideoSourceAudio:
    '__useRecorderSettings.autoSelectVideoSourceAudioAtom',
  curatedVoiceProvider: '__useRecorderSettings.curatedVoiceProviderAtom',
  curatedVoiceSourceId: '__useRecorderSettings.curatedVoiceSourceIdAtom',
  curatedVoiceVolume: '__useRecorderSettings.curatedVoiceVolumeAtom',
  outputPath: 'persistent__useRecorderSettings.outputPathAtom',
  recordName: '__useRecorderSettings.recordNameAtom',
  recordNameCount: '__useRecorderSettings.recordNameCountAtom',
  skipChecks: '__useRecorderSettings.skipCheks',
  latency: '__useRecorderSettings.latencyAtom',
  slicesOutputPath: 'persistent__useRecorderSettings.slicesOutputPathAtom',
  audioMix: '__useRecorderSettings.audioMixAtom',
  audioBitrate: '__useRecorderSettings.audioBitrateAtom',
  videoSourceCheckDisabled:
    '__useRecorderSettings.videoSourceCheckDisabledAtom',
  audioOutputCheckDisabled:
    '__useRecorderSettings.audioOutputCheckDisabledAtom',
  bufferModeAutoUploadSlices:
    '__useRecorderSettings.bufferModeAutoUploadSlicesAtom',
  processPriority: '__useAppSettings.processesPriorityAtom',
  toolbarEnabled: '__useRecorderSettings.isToolbarEnabledAtom',
  toolbarVisible: '__useRecorderSettings.isToolbarVisibleAtom',
  toolbarMaximized: '__useRecorderSettings.isToolbarMaximizedAtom',
  zoom: '__useZoom.zoomAtom',
  sidebarCollapsed: 'sidebar.collapsed',
  slicesActivated: '__useSlicesState.slicesIsActivatedAtom',
  rememberPlaytesterNames: '__usePlaytester.rememberNamesAtom',
  playtesterFirstName: '__usePlaytester.firstNameAtom',
  playtesterLastName: '__usePlaytester.lastNameAtom',
} as const;

export type RecorderPreferenceKey =
  (typeof recorderPreferenceKeys)[keyof typeof recorderPreferenceKeys];

export type RecorderPreferenceValues = Partial<
  Record<RecorderPreferenceKey, unknown>
>;

export type RecorderPreferencesSnapshot = {
  version: typeof recorderPreferencesVersion;
  values: RecorderPreferenceValues;
};

export type RecorderPreferencesMigrationResult = {
  version: typeof recorderPreferencesVersion;
  migratedKeys: RecorderPreferenceKey[];
  existingKeys: RecorderPreferenceKey[];
  rejectedKeys: string[];
};

const recorderPreferenceKeySet = new Set<string>(
  Object.values(recorderPreferenceKeys),
);

export function isRecorderPreferenceKey(
  value: string,
): value is RecorderPreferenceKey {
  return recorderPreferenceKeySet.has(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

function isNamedValue(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.name === 'string' &&
    isFiniteNumber(value.value)
  );
}

function isDevice(value: unknown): boolean {
  return (
    value === null ||
    (isRecord(value) &&
      typeof value.id === 'string' &&
      typeof value.name === 'string')
  );
}

function isResolution(value: unknown): boolean {
  if (
    !isRecord(value) ||
    !['res720', 'res1080', 'res1440', 'res2160'].includes(
      String(value.shortName),
    ) ||
    typeof value.name !== 'string' ||
    !isFiniteNumber(value.width) ||
    !isFiniteNumber(value.height) ||
    !Array.isArray(value.bitrates) ||
    value.bitrates.length === 0
  ) {
    return false;
  }

  return value.bitrates.every(isNamedValue);
}

const booleanPreferenceKeys = new Set<RecorderPreferenceKey>([
  recorderPreferenceKeys.isLive,
  recorderPreferenceKeys.isCursorEnabled,
  recorderPreferenceKeys.audioInputAutoGainControl,
  recorderPreferenceKeys.audioInputVoiceMasking,
  recorderPreferenceKeys.autoSelectVideoSourceAudio,
  recorderPreferenceKeys.skipChecks,
  recorderPreferenceKeys.videoSourceCheckDisabled,
  recorderPreferenceKeys.audioOutputCheckDisabled,
  recorderPreferenceKeys.bufferModeAutoUploadSlices,
  recorderPreferenceKeys.toolbarEnabled,
  recorderPreferenceKeys.toolbarVisible,
  recorderPreferenceKeys.toolbarMaximized,
  recorderPreferenceKeys.sidebarCollapsed,
  recorderPreferenceKeys.slicesActivated,
  recorderPreferenceKeys.rememberPlaytesterNames,
]);

export function isValidRecorderPreferenceValue(
  key: RecorderPreferenceKey,
  value: unknown,
): boolean {
  if (booleanPreferenceKeys.has(key)) {
    return typeof value === 'boolean';
  }

  switch (key) {
    case recorderPreferenceKeys.resolution:
      return isResolution(value);
    case recorderPreferenceKeys.bitrate:
    case recorderPreferenceKeys.liveBitrate:
    case recorderPreferenceKeys.audioBitrate:
      return isNamedValue(value) && (value as { value: number }).value > 0;
    case recorderPreferenceKeys.audioInputNoiseSuppression:
      return (
        isNamedValue(value) &&
        [-1, 0, 1, 2, 3].includes((value as { value: number }).value)
      );
    case recorderPreferenceKeys.processPriority:
      return (
        isNamedValue(value) &&
        [0, -14].includes((value as { value: number }).value)
      );
    case recorderPreferenceKeys.camera:
    case recorderPreferenceKeys.audioInput:
    case recorderPreferenceKeys.audioOutput:
      return isDevice(value);
    case recorderPreferenceKeys.cameraPosition:
      return (
        typeof value === 'string' &&
        ['top-left', 'top-right', 'bottom-left', 'bottom-right'].includes(value)
      );
    case recorderPreferenceKeys.audioInputPreference:
      return (
        typeof value === 'string' &&
        ['unset', 'default', 'selected', 'off'].includes(value)
      );
    case recorderPreferenceKeys.audioInputEchoSuppression:
      return (
        typeof value === 'string' &&
        ['disabled', 'low', 'moderate', 'high'].includes(value)
      );
    case recorderPreferenceKeys.curatedVoiceProvider:
      return (
        typeof value === 'string' &&
        ['off', 'discord', 'teams', 'slack', 'zoom', 'google_meet'].includes(
          value,
        )
      );
    case recorderPreferenceKeys.latency:
      return (
        typeof value === 'string' &&
        ['standard', 'reduced', 'low', 'ultra_low'].includes(value)
      );
    case recorderPreferenceKeys.microphoneVolume:
      return isFiniteNumber(value) && value >= 0 && value <= 2.5;
    case recorderPreferenceKeys.audioOutputVolume:
    case recorderPreferenceKeys.curatedVoiceVolume:
      return isFiniteNumber(value) && value >= 0 && value <= 2;
    case recorderPreferenceKeys.audioMix:
      return isFiniteNumber(value) && value >= 0 && value <= 1;
    case recorderPreferenceKeys.recordNameCount:
      return Number.isInteger(value) && (value as number) >= 0;
    case recorderPreferenceKeys.zoom:
      return (
        isFiniteNumber(value) &&
        [70, 80, 90, 100, 110, 125, 150, 175, 200].includes(value)
      );
    case recorderPreferenceKeys.outputPath:
    case recorderPreferenceKeys.slicesOutputPath:
    case recorderPreferenceKeys.curatedVoiceSourceId:
    case recorderPreferenceKeys.playtesterFirstName:
    case recorderPreferenceKeys.playtesterLastName:
      return isNullableString(value);
    case recorderPreferenceKeys.recordName:
      return typeof value === 'string';
    default:
      return false;
  }
}

export function sanitizeRecorderPreferenceValues(
  values: Record<string, unknown>,
): {
  values: RecorderPreferenceValues;
  rejectedKeys: string[];
} {
  const accepted: RecorderPreferenceValues = {};
  const rejectedKeys: string[] = [];

  Object.entries(values).forEach(([key, value]) => {
    if (!isRecorderPreferenceKey(key)) {
      rejectedKeys.push(key);
      return;
    }

    if (!isValidRecorderPreferenceValue(key, value)) {
      rejectedKeys.push(key);
      return;
    }

    accepted[key] = value;
  });

  return { values: accepted, rejectedKeys };
}

export const legacyMicrophoneVolumePreferenceKey =
  '__useRecorderSettings.audioInputVolumeAtom';

export const recorderPreferenceLegacyKeys: Readonly<
  Partial<Record<RecorderPreferenceKey, readonly string[]>>
> = {
  [recorderPreferenceKeys.microphoneVolume]: [
    recorderPreferenceKeys.microphoneVolume,
    legacyMicrophoneVolumePreferenceKey,
  ],
};

export const transientLocalStorageKeys = [
  '__useRecorderSettings.eventToUpload',
  '__useRecorderSettings.uploadName',
  '__useRecorderSettings.telemetryPath',
  '__useRecorderState.stateAtom',
  '__useRecorderState.streamKeyAtom',
  '__useRecorderState.streamUrlAtom',
  '__useRecorderState.livekitStreamJwtAtom',
  '__useRecorderState.leafIdAtom',
  '__useRecorderState.cameraAndAudioInputStreamKeyAtom',
  '__useRecorderState.chatVoiceStreamKeyAtom',
  '__useRecorderState.recorderReconnectedAtAtom',
  '__useRecorderState.recorderStartedAtAtom',
  '__useRecorderState.recorderPreviewStartedAtAtom',
  '__useRecorderState.recorderStoppedManuallyAtAtom',
  '__useRecorderState.recorderAudioInputMutedAtom',
  '__updateAvailable',
  '__updateAppVersion',
  '__currentAppVersion',
  '__releaseNotes',
  '__appUpdateComplete',
  '__useUploadingState.activeUpload',
  '__useUploadingState.progressUpload.v2',
  '__useUploadingState.uploadQueue',
  '__current_user',
  '_currentTeam',
  '_currentNode',
  '__useCurrentTeam.currentProjectPermissionsAtom',
  '__useCurrentTeam.currentProjectUsageStatusAtom',
  '__useSlicesSettings.slicesSettingsAtom',
  '__useSlicesSettings.minSlicesIntervalAtom',
  '__useChatState.isSpeakingAtom',
] as const;
